/**
 * What a send did (plan docs/plans/lms-plan.md 8.2 step 4, Appendix A POST
 * /admin/campaigns, 18.16; WP-43): the outcome table (assigned, already
 * assigned, already current, skipped inactive, each with its count and what it
 * means), a warning when some notices could not be written (the assignments
 * stand), and the way to the campaign's per-person table.
 */
import { Link } from 'react-router-dom';
import { CheckCircle, Send } from 'lucide-react';
import { primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { CAMPAIGN_OUTCOMES } from '../../../api/campaigns';
import type { SendCampaignResult } from '../../../api/campaigns';
import {
  cardCls,
  OUTCOME_HINTS,
  OutcomePill,
  peopleCount,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
  WarningNote,
} from '../../../components/pickers/shared';
import { campaignHref } from '../campaigns/labels';

export interface SendResultProps {
  result: SendCampaignResult;
  courseTitle: string;
  onSendAnother: () => void;
}

export function SendResult({ result, courseTitle, onSendAnother }: SendResultProps) {
  const total = CAMPAIGN_OUTCOMES.reduce((n, o) => n + (result.outcomes?.[o] ?? 0), 0);
  return (
    <div className="space-y-4" data-testid="send-result">
      <div className={`${cardCls} flex items-start gap-3`}>
        <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Sent</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">{`${courseTitle} went out to ${peopleCount(total)}.`}</p>
        </div>
      </div>

      {result.notifications_failed > 0 && (
        <WarningNote testId="send-notifications-failed">
          {`${peopleCount(result.notifications_failed)} did not get the notice. Their assignments stand and show on their Home tab.`}
        </WarningNote>
      )}

      <div className={tableCardCls}>
        <table className={tableCls} data-testid="send-outcomes">
          <thead className={theadCls}>
            <tr>
              <th scope="col" className={thCls}>
                Outcome
              </th>
              <th scope="col" className={`${thCls} text-right`}>
                People
              </th>
              <th scope="col" className={thCls}>
                What it means
              </th>
            </tr>
          </thead>
          <tbody className={tbodyCls}>
            {CAMPAIGN_OUTCOMES.map((o) => (
              <tr key={o} data-testid={`send-outcome-${o}`}>
                <td className={tdCls}>
                  <OutcomePill outcome={o} />
                </td>
                <td className={`${tdCls} text-right tabular-nums`}>{(result.outcomes?.[o] ?? 0).toLocaleString()}</td>
                <td className={`${tdCls} text-gray-600 dark:text-gray-300`}>{OUTCOME_HINTS[o]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Link to={campaignHref(result.campaign.id)} className={primaryBtnCls} data-testid="open-campaign">
          Open the campaign
        </Link>
        <button type="button" className={secondaryBtnCls} onClick={onSendAnother} data-testid="send-another">
          <Send className="h-4 w-4" />
          Send another
        </button>
      </div>
    </div>
  );
}

export default SendResult;
